import { Card, Spinner, Table } from "reactstrap";

import NoResultFoundSearch from "Components/CommonComponents/NoResultFoundSearch/NoResultFoundSearch";
import NoDataFoundIcon from "Assets/Icons/NoDataFoundIcon";

import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import styled from "styled-components";
import {
  ColorCodeInterface,
  RegionMapping,
  RiskColorCode,
} from "utilFunctions/Constants/Constants";
import DownArrow from "Assets/Icons/downArrow30.png";
import UpArrow from "Assets/Icons/upArrow30.png";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  capitalizeWords,
  roundToAnyDecimalPlaces,
} from "utilFunctions/helperFunctions/utilFunctions";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
const SORT_ASC = "ASC";
const SORT_DESC = "DESC";
export const RiskStyledDiv = styled<any>("div")`
  background-color: ${(props: any) => props.color};
  border-radius: 4px;
  padding: 4px;
  width: fit-content;
`;
export const RiskStyledDSpan = styled<any>("span")`
  color: ${(props: any) => props.color};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
`;
export const displayNumberWithDecimals = (number: any) => {
  if (Number.isInteger(number)) {
    return number.toString(); // Return the integer as a string
  } else {
    return number.toFixed(0); // Return the number with two decimals
  }
};
export const returnRiskSegment = (segment: string) => {
  const colorCode = RiskColorCode.find(
    (item: ColorCodeInterface) => item.name === segment
  );
  return (
    <RiskStyledDiv color={colorCode?.bgColor}>
      <RiskStyledDSpan color={colorCode?.color}>
        {segment == "NA" ? "Low" : segment}
      </RiskStyledDSpan>
    </RiskStyledDiv>
  );
};
interface IBranchHealthTable {
  searchedFaculty?: any;
  mode?: string;
  tableloading?: any;
  tableHeading?: any;
  tableData?: any;
  setSortColumn: any;
  sortColumn: string;
  setSortOrder: any;
  sortOrder: string;
  filterState?: any;
  className?: any;
}
const BranchHealthTable = ({
  tableloading,
  tableHeading,
  tableData,
  sortColumn,
  setSortColumn,
  sortOrder,
  setSortOrder,
  filterState,
  className,
}: IBranchHealthTable) => {
  const { screen }: any = useWindowSize();
  const navigate = useNavigate();

  const returnStatus = (status: string, item: any) => {
    const formattedDate = moment(item.date).format("D MMM YYYY");
    return (
      <RiskStyledDiv color={status === "1" ? "#E6F4ED" : "#FCEEED"}>
        <RiskStyledDSpan color={status === "1" ? "#05945B" : "#E7685A"}>
          {status === "1" ? "Active" : "Left on " + formattedDate}
        </RiskStyledDSpan>
      </RiskStyledDiv>
    );
  };
  const routeToMyBranch = (item: any) => {
    navigate("/admin/myBranch/" + item.branchCode, {
      replace: false,
      state: {
        selectedBranch: item,
        filterState: filterState,
      },
    });
  };
  const routeToPerformance = (item: any) => {
    const lopStudentDetailsViewEvent = {
      event: "ag_lop_view_student_lop_details_on_perf_page",
      PSID: String(item?.psid),
      CURRENT_LOP_RISK_SEGMENT: item?.segment,
      LOP_RISK_PERCENTAGE: item?.probability != null ? item.probability : null,
    };
    pushToDataLayerForGTM(lopStudentDetailsViewEvent);
    navigate("/admin/performance/" + item.psid, {
      replace: false,
      state: {
        selectedStudent: item,
      },
    });
  };

  const renderColumn = (item: any, value: any, columnValue: any) => {
    switch (columnValue) {
      case "branchName":
        return (
          <td>
            <span className="filterSpan" onClick={() => routeToMyBranch(item)}>
              {value}
            </span>
          </td>
        );
      case "name":
        return (
          <td>
            <span
              className="filterSpan"
              onClick={() => routeToPerformance(item)}
            >
              {capitalizeWords(value)}
            </span>
          </td>
        );
      case "branchCode":
        return <td>{value}</td>;
      case "region":
        return <td>{RegionMapping[value]}</td>;
      case "active":
        return <td>{value}</td>;
      case "status":
        return <td>{returnStatus(value, item)}</td>;
      case "segment":
        return <td>{returnRiskSegment(value)}</td>;
      case "probability":
        return (
          <td>
            {roundToAnyDecimalPlaces(
              Number(displayNumberWithDecimals(value * 100)),
              2
            )}{" "}
            %
          </td>
        );
      default:
        return <td>{value}</td>;
    }
  };
  const handleSort = (column: any) => {
    sortOrder === SORT_ASC ? setSortOrder(SORT_DESC) : setSortOrder(SORT_ASC);
    setSortColumn(column);
  };
  return (
    <div
      className={
        ["xs", "sm", "md"].includes(screen)
          ? className
            ? className
            : "batchTableContainerMB"
          : className
          ? className
          : "batchTableContainer pl-5"
      }
    >
      <Card className="mb-0 table-border-custom">
        <div className="tableFixHead">
          {!tableloading && tableData && tableData.length === 0 ? (
            <div className="mt-4 mb-8">
              <NoResultFoundSearch
                primaryMessage="LOP data not available"
                secondaryMessage={
                  "LOP data has not been synced in the system yet or you don't have any allotted branches in the system.                  "
                }
              >
                <NoDataFoundIcon />
              </NoResultFoundSearch>
            </div>
          ) : (
            <Table responsive className="table table-hover table-border-custom">
              <thead className="table-light-custom border-top table-border-custom">
                {tableHeading.map(
                  (column: { label: string; value: string }, index: number) => {
                    return (
                      <th
                        key={index}
                        onClick={() => {
                          if (
                            [
                              "region",
                              "active",
                              "moderateRisk",
                              "highRisk",
                              "alarmingRisk",
                              "probability",
                            ].includes(column.value)
                          ) {
                            handleSort(column.value);
                          }
                        }}
                      >
                        <div className="d-flex align-items-center gap-1 border-bottom-0">
                          <div>{column.label}</div>
                          {[
                            "region",
                            "active",
                            "moderateRisk",
                            "highRisk",
                            "alarmingRisk",
                            "probability",
                          ].includes(column.value) ? (
                            <span
                              className="cursor-pointer"
                              style={{ width: "20px" }}
                            >
                              {sortColumn == column.value &&
                              sortOrder === SORT_ASC ? (
                                <img width={16} height={12} src={UpArrow} />
                              ) : (
                                <img width={16} height={12} src={DownArrow} />
                              )}
                            </span>
                          ) : null}
                        </div>
                      </th>
                    );
                  }
                )}
              </thead>
              <tbody>
                {tableloading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="d-flex justify-content-center">
                        <Spinner color="info" />
                      </div>
                    </td>
                  </tr>
                ) : (
                  !tableloading &&
                  tableData &&
                  Array.isArray(tableData) &&
                  tableData.map((branchItem: any, index: any) => {
                    return (
                      <tr key={index}>
                        {tableHeading.map(
                          (column: { label: string; value: string }) => (
                            <>
                              {" "}
                              {renderColumn(
                                branchItem,
                                branchItem[column.value],
                                column.value
                              )}
                            </>
                          )
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};
export default BranchHealthTable;
